import React, { useState } from 'react';
import { Database, Users, Package, GraduationCap, ShoppingBag, FileText, Utensils, ShoppingCart, Car, DollarSign, Dumbbell, Zap, Printer } from 'lucide-react';

const Solutions = () => {
  const [activeTab, setActiveTab] = useState('enterprise');

  const enterpriseSolutions = [
    {
      icon: Users,
      title: 'CRM Solution',
      description: 'Track leads, convert customers, and automate relationships with intelligent pipeline management.',
      link: '/crm-solution'
    },
    {
      icon: Database,
      title: 'ERP Solution',
      description: 'Optimize multi-department operations, inventory, and supply chain with real-time data integration.',
      link: '/erp-solution'
    },
    {
      icon: Package,
      title: 'HRM Solution',
      description: 'Streamline workforce operations by automating hiring, payroll, attendance, and performance tracking.',
      link: '/hrm-solution'
    },
    {
      icon: GraduationCap,
      title: 'LMS Solution',
      description: 'Deploy online courses, track employee/student certification, and deliver interactive video content.',
      link: '/lms-solution'
    },
    {
      icon: ShoppingBag,
      title: 'POS Solution',
      description: 'Streamline multi-store retail, billing, barcode scanning, and inventory sync in real-time.',
      link: '/pos-solution'
    },
    {
      icon: FileText,
      title: 'CMS Solution',
      description: 'Manage, publish, and localize high-volume digital content without writing code.',
      link: '/cms-solution'
    }
  ];

  const appSolutions = [
    {
      icon: Utensils,
      title: 'Food Delivery Platform',
      description: 'Custom on-demand food ordering with live GPS tracking, restaurant dashboards, and driver apps.',
      link: '/contact'
    },
    {
      icon: ShoppingCart,
      title: 'Grocery Delivery App',
      description: 'Hyperlocal store catalog, rapid checkout, inventory sync, and scheduled delivery slots.',
      link: '/contact'
    },
    {
      icon: Car,
      title: 'Taxi & Mobility Booking',
      description: 'Real-time ride matching, automated fare calculation, driver verification, and fleet tracking.',
      link: '/contact'
    },
    {
      icon: DollarSign,
      title: 'Fintech & Wallet Solution',
      description: 'Secure peer-to-peer transfers, digital wallet integration, micro-lending, and KYC verification.',
      link: '/contact'
    },
    {
      icon: Dumbbell,
      title: 'Fitness & Gym Application',
      description: 'Workout planning, biometric tracking, live video streaming, and membership subscription billing.',
      link: '/contact'
    },
    {
      icon: Zap,
      title: 'Astrology & Consultation App',
      description: 'Kundli generation, live astrologer chat/call, horoscope calculations, and consultation booking.',
      link: '/contact'
    }
  ];

  const activeSolutions = activeTab === 'enterprise' ? enterpriseSolutions : appSolutions;

  return (
    <section id="solutions" className="py-24 bg-slate-50 text-left relative overflow-hidden border-y border-gray-200">
      <div className="container mx-auto px-6 relative z-10">

        {/* Section Header */}
        <div className="text-center mb-12 max-w-3xl mx-auto">
          <div className="inline-flex items-center space-x-2 bg-cyan-100/80 border border-cyan-200 rounded-full px-4 py-1.5 mb-4 shadow-sm">
            <span className="text-cyan-800 text-sm font-mono font-bold uppercase tracking-wider">
              Tailored Product Frameworks
            </span>
          </div>
          <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-4 leading-tight">
            Turnkey <span className="bg-gradient-to-r from-cyan-600 via-blue-600 to-orange-500 bg-clip-text text-transparent">On-Demand & Enterprise Solutions</span>
          </h2>
          <p className="text-gray-600 text-base md:text-lg leading-relaxed">
            Accelerate your time-to-market with pre-architected, highly customizable solution modules engineered for enterprise reliability.
          </p>

          {/* Toggle Tabs */}
          <div className="flex justify-center mt-8">
            <div className="bg-white border border-gray-200 p-1.5 rounded-2xl inline-flex gap-2 shadow-sm">
              <button
                onClick={() => setActiveTab('enterprise')}
                className={`px-6 py-2.5 rounded-xl text-sm md:text-sm font-bold transition-all duration-300 ${activeTab === 'enterprise'
                  ? 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                Enterprise Solutions
              </button>
              <button
                onClick={() => setActiveTab('apps')}
                className={`px-6 py-2.5 rounded-xl text-sm md:text-sm font-bold transition-all duration-300 ${activeTab === 'apps'
                  ? 'bg-gradient-to-r from-orange-500 to-amber-600 text-white shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                App & On-Demand Solutions
              </button>
            </div>
          </div>
        </div>

        {/* Solutions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {activeSolutions.map((solution, index) => (
            <a
              key={index}
              href={solution.link}
              className="group relative bg-white border border-gray-200 hover:border-cyan-500/80 rounded-2xl p-8 transition-all duration-300 hover:scale-[1.02] shadow-sm hover:shadow-xl hover:shadow-cyan-500/10 flex flex-col justify-between"
            >
              <div>
                <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-md">
                  <solution.icon className="w-7 h-7 text-white" />
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-cyan-600 transition-colors">
                  {solution.title}
                </h3>

                <p className="text-gray-600 text-base leading-relaxed mb-6">
                  {solution.description}
                </p>
              </div>

              <div className="flex items-center text-base font-mono font-bold text-cyan-600 group-hover:text-cyan-700">
                <span>View Specs</span>
                <span className="ml-1 group-hover:translate-x-1 transition-transform">→</span>
              </div>
            </a>
          ))}
        </div>

      </div>
    </section>
  );
};

export default Solutions;